import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Check, LogOut, Download } from "lucide-react";
import axios from "axios";
import { API, getDeviceId } from "@/lib/api";

const downloadMyData = async () => {
  const { data } = await axios.get(`${API}/account/export`, { params: { device_id: getDeviceId() } });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
  a.download = "nice-my-data.json";
  a.click();
};
import { toast } from "sonner";
import { SimplePage } from "@/components/SimplePage";
import { useAuth, isMember, MEMBER_STATUSES, startCheckout, cancelMembership, resumeMembership, paymentStatus, redeemCode } from "@/lib/auth";
import { errorText } from "@/lib/family";
import { VoicePicker } from "@/components/VoicePicker";
import { WhyNice } from "@/components/WhyNice";
import { ShareExperience } from "@/components/ShareExperience";
import { DeleteAccount } from "@/components/DeleteAccount";
import { Nice, niceText } from "@/components/Nice";

const PERKS = ["The “How can I help?” manners coach", "Everyday Manners practice scenes", "The 3 Steps of an Apology", "Family Dinner Game & printables", "BurrHaus card library", "Siri & Alexa setup"];

export function Subscribe() {
  const { user, setUser, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  const redeem = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setUser(await redeemCode(code)); toast.success("Complimentary access unlocked. Welcome to NICE!"); navigate("/"); } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const go = async () => { setBusy(true); try { await startCheckout(); } catch (e) { toast.error(errorText(e)); setBusy(false); } };
  if (isMember(user)) return <Account />;
  return (
    <SimplePage testId="subscribe-page" back="/about" backLabel="About NICE">
      <section className="pp-card max-w-lg" data-testid="subscribe-card">
        <p className="pp-label"><Nice />™ Family Membership</p>
        <h1 className="pp-h1 pp-sorry-title">3 days free, then $7.99/month</h1>
        <ul className="pp-steps">{PERKS.map((p) => <li key={p}><span><Check className="h-4 w-4" /></span><p>{p}</p></li>)}</ul>
        <p className="pp-sub text-sm">You won't be charged until your trial ends. Cancel anytime from your account.</p>
        <div className="pp-actions">
          <button className="pp-primary" onClick={go} disabled={busy} data-testid="start-trial-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Start my free trial"}
          </button>
          <button className="pp-ghost" onClick={logout} data-testid="subscribe-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
        <form onSubmit={redeem} className="mt-6" data-testid="access-code-form">
          <p className="pp-label">Have a free-access code?</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input className="pp-input pp-auth-input flex-1 min-w-0" style={{ marginTop: 8 }} value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="Enter code" data-testid="access-code-input" />
            <button type="submit" className="pp-ghost" disabled={busy || !code.trim()} data-testid="access-code-submit">Redeem</button>
          </div>
        </form>
      </section>
      <WhyNice className="mt-6 max-w-xl" />
    </SimplePage>
  );
}

export function Account() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const act = async (fn) => { setBusy(true); try { setUser(await fn()); } catch (e) { toast.error(errorText(e)); } setBusy(false); };
  const end = user.period_end ? new Date(user.period_end).toLocaleDateString() : "";
  const store = { APP_STORE: "the App Store", MAC_APP_STORE: "the App Store", PLAY_STORE: "Google Play" }[user.mobile_store] || "the NICE app";
  const mobileOnly = user.is_member && !MEMBER_STATUSES.includes(user.subscription_status) && !user.complimentary;
  const label = mobileOnly ? `Active via ${store}` : user.complimentary ? "Complimentary access" : { trialing: "Free trial", active: "Active", past_due: "Payment past due", canceled: "Canceled" }[user.subscription_status] || "No membership";
  return (
    <SimplePage testId="account-page">
      <section className="pp-card max-w-lg" data-testid="account-card">
        <p className="pp-label">Account & Subscription</p>
        <h1 className="pp-h1 pp-sorry-title">Hi, {user.name}</h1>
        <p className="pp-sub" data-testid="account-email">{user.email}</p>
        <p className="pp-reward mt-6" data-testid="account-status">
          {label}{end && (user.cancel_at_period_end ? ` · access ends ${end}` : ` · ${user.subscription_status === "trialing" ? "first charge" : "renews"} ${end}`)}
        </p>
        <div className="pp-actions">
          {mobileOnly && <p className="text-sm opacity-75 mt-2" data-testid="account-mobile-note">Manage or cancel this membership in {store} settings on your phone.</p>}
          {MEMBER_STATUSES.includes(user.subscription_status) && !user.complimentary && !user.cancel_at_period_end && (
            <button className="pp-ghost" disabled={busy} data-testid="cancel-membership-button"
              onClick={() => window.confirm("Cancel your membership? You'll keep access until the end of this billing period.") && act(cancelMembership)}>
              Cancel membership
            </button>
          )}
          {isMember(user) && user.cancel_at_period_end && (
            <button className="pp-primary" disabled={busy} onClick={() => act(resumeMembership)} data-testid="resume-membership-button">Keep my membership</button>
          )}
          {!isMember(user) && <Link to="/subscribe" className="pp-primary" data-testid="account-subscribe-link">Subscribe</Link>}
          <button className="pp-ghost" onClick={downloadMyData} data-testid="download-data-button"><Download className="h-4 w-4" /> Download my data</button>
          <button className="pp-ghost" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </section>
      {isMember(user) && <div className="max-w-lg mt-6"><VoicePicker /></div>}
      {isMember(user) && <ShareExperience />}
      <DeleteAccount />
      {user.is_admin && <Link to="/admin/testimonials" className="pp-ghost mt-6" data-testid="admin-testimonials-link">Manage "What parents say" quotes</Link>}
    </SimplePage>
  );
}

export function PaymentResult({ ok }) {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(ok ? "checking" : "canceled");
  useEffect(() => {
    if (!ok) return;
    const id = params.get("session_id");
    let n = 0;
    let timer;
    let active = true;
    const poll = async () => {
      const s = await paymentStatus(id).catch(() => null);
      if (!active) return;
      if (s?.payment_status === "paid") { await refreshUser(); setState("done"); }
      else if (++n < 15) timer = setTimeout(poll, 2000);
      else setState("slow");
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [ok, params, refreshUser]);
  const msg = { checking: "Confirming your membership…", done: "Welcome to NICE! Your free trial has started.", slow: "Still confirming. Refresh in a minute.", canceled: "Checkout canceled. You can start your trial anytime." }[state];
  return (
    <SimplePage testId="payment-result-page">
      <section className="pp-card max-w-lg" data-testid="payment-result-card">
        <h1 className="pp-h1 pp-sorry-title">{state === "done" ? "You're in!" : ok ? "Almost there" : "No worries"}</h1>
        <p className="pp-sub" data-testid="payment-result-message">{state === "checking" && <Loader2 className="inline h-4 w-4 animate-spin mr-2" />}{niceText(msg)}</p>
        <div className="pp-actions">
          <Link to={state === "done" ? "/" : "/subscribe"} className="pp-primary" data-testid="payment-continue-link">{state === "done" ? <>Start using <Nice /></> : "Back"}</Link>
        </div>
      </section>
    </SimplePage>
  );
}
